import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Shield, LayoutGrid, Network, Mail, Wrench, Building, 
  Users, FileText, Bell, BarChart3, Globe, Lock, Clock,
  ArrowRight, Smartphone, Cloud, Bot
} from 'lucide-react';
import DemoRequestModal from './DemoRequestModal';

type LandingProps = {
  onGetStarted?: () => void;
  onLogin?: () => void;
};

const Feature: React.FC<{icon: React.ReactNode; title: string; desc: string; color?: string}> = ({ icon, title, desc, color = "bg-black" }) => (
  <div className="group rounded-2xl border border-gray-200 bg-white p-6 shadow-sm hover:shadow-lg transition-all duration-300 hover:border-gray-300">
    <div className={`w-12 h-12 rounded-xl ${color} text-white flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300`}>
      {icon}
    </div>
    <h3 className="text-xl font-semibold text-gray-900 mb-2">{title}</h3>
    <p className="text-gray-600 leading-relaxed">{desc}</p>
  </div>
);

const Landing: React.FC<LandingProps> = ({ onGetStarted, onLogin }) => {
  const navigate = useNavigate();
  const [openDemo, setOpenDemo] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const goSignUp = () => (onGetStarted ? onGetStarted() : navigate('/cadastro'));
  const goLogin = () => (onLogin ? onLogin() : navigate('/login'));

  useEffect(() => {
    const originalOverflow = document.body.style.overflow;
    if (mobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = originalOverflow || '';
    }
    return () => {
      document.body.style.overflow = originalOverflow || '';
    };
  }, [mobileMenuOpen]);

  // Preços e planos removidos da landing para esta fase

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50">
  {/* Header */}
  <header className="sticky top-0 z-30 bg-white/80 backdrop-blur-lg border-b border-gray-200 shadow-sm">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-r from-gray-900 to-black text-white flex items-center justify-center font-bold text-lg shadow-lg">E</div>
            <span className="font-bold text-xl text-gray-900">Escalone</span>
          </div>
          <nav className="hidden md:flex items-center gap-8 text-sm">
            <a href="#features" className="text-gray-700 hover:text-black transition-colors font-medium">Recursos</a>
            <a href="#solutions" className="text-gray-700 hover:text-black transition-colors font-medium">Soluções</a>
            <button onClick={goLogin} className="rounded-lg px-4 py-2 border border-gray-300 hover:border-black transition-all font-medium">Entrar</button>
            <button onClick={goSignUp} className="rounded-lg px-4 py-2 bg-gradient-to-r from-gray-900 to-black text-white hover:from-black hover:to-gray-900 transition-all font-medium shadow-lg">Get Started</button>
          </nav>
          {/* Mobile menu button */}
          <button
            className="md:hidden inline-flex items-center gap-2 rounded-lg px-3 py-2 border border-gray-300 text-gray-700 hover:bg-gray-50"
            onClick={() => setMobileMenuOpen(true)}
            aria-label="Abrir menu"
          >
            <span>Menu</span>
          </button>
        </div>
      </header>

      {/* Mobile menu overlay (fora do header para não ser recortado) */}
      {mobileMenuOpen && (
        <div className="fixed inset-0 z-[100] md:hidden">
          <div className="absolute inset-0 bg-black/40" onClick={() => setMobileMenuOpen(false)} />
          <div className="absolute right-0 top-0 h-full w-80 max-w-[85%] bg-white shadow-2xl p-6 flex flex-col gap-4">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg bg-gradient-to-r from-gray-900 to-black text-white flex items-center justify-center font-bold">E</div>
                <span className="font-semibold text-gray-900">Escalone</span>
              </div>
              <button className="text-gray-500 hover:text-gray-800" onClick={() => setMobileMenuOpen(false)} aria-label="Fechar menu">✕</button>
            </div>
            <a href="#features" onClick={() => setMobileMenuOpen(false)} className="px-3 py-2 rounded-lg hover:bg-gray-50 text-gray-800">Recursos</a>
            <a href="#solutions" onClick={() => setMobileMenuOpen(false)} className="px-3 py-2 rounded-lg hover:bg-gray-50 text-gray-800">Soluções</a>
            <div className="mt-2 flex flex-col gap-3">
              <button onClick={() => { setMobileMenuOpen(false); goLogin(); }} className="w-full rounded-lg px-4 py-2 border border-gray-300 hover:border-black transition-all font-medium">Entrar</button>
              <button onClick={() => { setMobileMenuOpen(false); goSignUp(); }} className="w-full rounded-lg px-4 py-2 bg-gradient-to-r from-gray-900 to-black text-white hover:from-black hover:to-gray-900 transition-all font-medium shadow-lg">Get Started</button>
            </div>
          </div>
        </div>
      )}

      {/* Hero (visual banners removidos) */}
      <section className="relative">
        <div className="relative mx-auto max-w-7xl px-4 sm:px-6 py-16 sm:py-20 lg:py-24">
          <div className="grid lg:grid-cols-1 gap-8 lg:gap-12 items-center">
            <div className="text-center">
              <div className="inline-flex items-center gap-2 rounded-full border border-blue-200 bg-blue-50 px-4 py-2 text-sm text-blue-700 mb-6">
                <Shield className="w-4 h-4" /> Gestão moderna de processos empresariais
              </div>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-gray-900 leading-tight">
                Transforme seus 
                <span className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent"> fluxos de trabalho</span>
              </h1>
              <p className="mt-6 text-xl text-gray-600 leading-relaxed max-w-2xl mx-auto">
                O Escalone é a plataforma completa para organizar equipes, automatizar processos e dar visibilidade total aos seus negócios com Kanban inteligente, organogramas dinâmicos e automações poderosas.
              </p>
              <div className="mt-8 flex flex-col sm:flex-row gap-4 justify-center">
                <button onClick={goSignUp} className="rounded-xl bg-gradient-to-r from-blue-600 to-purple-600 text-white px-8 py-4 font-semibold text-lg hover:from-blue-700 hover:to-purple-700 transition-all shadow-xl hover:shadow-2xl transform hover:scale-105">
                  Começar grátis agora <ArrowRight className="inline w-5 h-5 ml-2" />
                </button>
                <button onClick={goLogin} className="rounded-xl border-2 border-gray-300 px-8 py-4 font-semibold text-lg hover:border-gray-900 transition-all bg-white hover:bg-gray-50">
                  Já tenho conta
                </button>
              </div>
              {/* Removido texto de teste grátis por 14 dias */}
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section id="features" className="py-16 sm:py-20 bg-white">
        <div className="mx-auto max-w-7xl px-4 sm:px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-extrabold text-gray-900 mb-4">Recursos que fazem a diferença</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">Descubra por que milhares de empresas escolhem o Escalone para gerenciar seus processos e equipes.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            <Feature 
              icon={<LayoutGrid className="w-6 h-6" />} 
              title="Kanban Inteligente" 
              desc="Quadros personalizáveis com arrastar e soltar, etapas customizadas, campos dinâmicos e controle de prazos automatizado."
              color="bg-blue-600"
            />
            <Feature 
              icon={<Network className="w-6 h-6" />} 
              title="Organograma Dinâmico" 
              desc="Visualize a hierarquia da empresa em tempo real, encontre responsáveis rapidamente e gerencie permissões por cargo."
              color="bg-green-600"
            />
            <Feature 
              icon={<Bot className="w-6 h-6" />} 
              title="Automações Avançadas" 
              desc="Crie gatilhos inteligentes para e-mails, movimentação de cards, notificações e integrações com sistemas externos."
              color="bg-purple-600"
            />
            <Feature 
              icon={<Mail className="w-6 h-6" />} 
              title="E-mails Automáticos" 
              desc="Templates personalizados, envio automático por vencimento, notificações de mudanças e relatórios por e-mail."
              color="bg-orange-600"
            />
            <Feature 
              icon={<Bell className="w-6 h-6" />} 
              title="Notificações Inteligentes" 
              desc="Alertas em tempo real, notificações push, resumos diários e lembretes personalizados por usuário."
              color="bg-red-600"
            />
            <Feature 
              icon={<BarChart3 className="w-6 h-6" />} 
              title="Dashboard Analytics" 
              desc="Métricas em tempo real, relatórios customizáveis, gráficos interativos e insights de produtividade."
              color="bg-indigo-600"
            />
            <Feature 
              icon={<FileText className="w-6 h-6" />} 
              title="Gestão de Documentos" 
              desc="Upload de arquivos, versionamento automático, integração com Google Drive e controle de acesso."
              color="bg-teal-600"
            />
            <Feature 
              icon={<Users className="w-6 h-6" />} 
              title="Colaboração Avançada" 
              desc="Comentários em tempo real, menções, histórico de atividades e chat integrado por card."
              color="bg-pink-600"
            />
            <Feature 
              icon={<Smartphone className="w-6 h-6" />} 
              title="App Mobile Nativo" 
              desc="Acesso completo via mobile, notificações push, modo offline e sincronização automática."
              color="bg-gray-600"
            />
          </div>
        </div>
      </section>

      {/* Solutions */}
      <section id="solutions" className="py-16 sm:py-20 bg-gradient-to-br from-gray-50 to-blue-50">
        <div className="mx-auto max-w-7xl px-4 sm:px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-extrabold text-gray-900 mb-4">Soluções para cada tipo de negócio</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">Independente do seu setor, o Escalone se adapta às suas necessidades específicas.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
            <div className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-100">
              <div className="w-12 h-12 rounded-xl bg-blue-100 text-blue-600 flex items-center justify-center mb-4">
                <Building className="w-6 h-6" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Manufatura</h3>
              <p className="text-gray-600 text-sm mb-4">Controle de produção, qualidade e entregas</p>
              <ul className="text-xs text-gray-500 space-y-1">
                <li>• Controle de estoque</li>
                <li>• Gestão de qualidade</li>
                <li>• Rastreamento de pedidos</li>
              </ul>
            </div>
            <div className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-100">
              <div className="w-12 h-12 rounded-xl bg-green-100 text-green-600 flex items-center justify-center mb-4">
                <Wrench className="w-6 h-6" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Serviços</h3>
              <p className="text-gray-600 text-sm mb-4">Gestão de projetos e atendimento ao cliente</p>
              <ul className="text-xs text-gray-500 space-y-1">
                <li>• Tickets de suporte</li>
                <li>• Projetos customizados</li>
                <li>• SLA e prazos</li>
              </ul>
            </div>
            <div className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-100">
              <div className="w-12 h-12 rounded-xl bg-purple-100 text-purple-600 flex items-center justify-center mb-4">
                <Globe className="w-6 h-6" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">E-commerce</h3>
              <p className="text-gray-600 text-sm mb-4">Vendas online e logística integrada</p>
              <ul className="text-xs text-gray-500 space-y-1">
                <li>• Gestão de pedidos</li>
                <li>• Controle de envios</li>
                <li>• Atendimento pós-venda</li>
              </ul>
            </div>
            <div className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-100">
              <div className="w-12 h-12 rounded-xl bg-orange-100 text-orange-600 flex items-center justify-center mb-4">
                <FileText className="w-6 h-6" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Consultoria</h3>
              <p className="text-gray-600 text-sm mb-4">Projetos complexos e gestão de clientes</p>
              <ul className="text-xs text-gray-500 space-y-1">
                <li>• Gestão de propostas</li>
                <li>• Controle de horas</li>
                <li>• Relatórios executivos</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

  {/* Pricing removido */}

  {/* Testimonials & Stats removed for pre-launch */}

      {/* CTA Section */}
      <section className="py-16 sm:py-20 bg-gradient-to-r from-gray-900 via-black to-gray-900">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 text-center text-white">
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-extrabold mb-6">
            Pronto para transformar seus processos?
          </h2>
          <p className="text-xl text-gray-300 mb-8 max-w-2xl mx-auto">
            Estamos em MVP: centralize seus processos em um só lugar e nos ajude a priorizar as próximas funcionalidades com seu feedback. Comece já hoje.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button onClick={goSignUp} className="w-full sm:w-auto rounded-xl bg-gradient-to-r from-blue-600 to-purple-600 text-white px-8 py-4 font-semibold text-lg hover:from-blue-700 hover:to-purple-700 transition-all shadow-xl hover:shadow-2xl transform hover:scale-105">
              Comece já hoje <ArrowRight className="inline w-5 h-5 ml-2" />
            </button>
            <button 
              onClick={() => setOpenDemo(true)} 
              className="w-full sm:w-auto rounded-xl border-2 border-white text-white px-8 py-4 font-semibold text-lg hover:bg-white hover:text-gray-900 transition-all"
            >
              Agendar demonstração
            </button>
          </div>
          <p className="mt-4 text-sm text-gray-400">Sem compromisso • Configuração em 5 minutos • Suporte dedicado</p>
        </div>
      </section>
      <DemoRequestModal open={openDemo} onClose={() => setOpenDemo(false)} />

      {/* Footer */}
      <footer className="py-16 bg-gray-900 text-white">
        <div className="mx-auto max-w-7xl px-4 sm:px-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-8">
            <div className="md:col-span-2">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-r from-blue-600 to-purple-600 text-white flex items-center justify-center font-bold text-lg">E</div>
                <span className="font-bold text-xl">Escalone</span>
              </div>
              <p className="text-gray-400 mb-6 max-w-md leading-relaxed">
                A plataforma completa para transformar seus processos empresariais em fluxos organizados, automáticos e inteligentes.
              </p>
              <div className="flex gap-4">
                <div className="flex items-center gap-2 text-sm text-gray-400">
                  <Lock className="w-4 h-4" />
                  <span>Dados seguros</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-gray-400">
                  <Cloud className="w-4 h-4" />
                  <span>Cloud Brasil</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-gray-400">
                  <Clock className="w-4 h-4" />
                  <span>99.9% uptime</span>
                </div>
              </div>
            </div>
            
            <div>
              <h3 className="font-semibold mb-4">Produto</h3>
              <ul className="space-y-2 text-sm text-gray-400">
                <li><a href="#features" className="hover:text-white transition-colors">Recursos</a></li>
                <li><a href="#solutions" className="hover:text-white transition-colors">Soluções</a></li>
                
                <li><a href="#" className="hover:text-white transition-colors">Integrações</a></li>
                <li><a href="#" className="hover:text-white transition-colors">API</a></li>
              </ul>
            </div>
            
            <div>
              <h3 className="font-semibold mb-4">Empresa</h3>
              <ul className="space-y-2 text-sm text-gray-400">
                <li><a href="#" className="hover:text-white transition-colors">Sobre nós</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Blog</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Carreiras</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Contato</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Suporte</a></li>
              </ul>
            </div>
          </div>
          
          <div className="border-t border-gray-800 pt-8 flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-sm text-gray-400">© {new Date().getFullYear()} Escalone by Refricril. Todos os direitos reservados.</p>
            <div className="flex items-center gap-6 text-sm text-gray-400">
              <a href="#" onClick={(e) => { e.preventDefault(); navigate('/termos'); }} className="hover:text-white transition-colors">Privacidade</a>
              <a href="#" onClick={(e) => { e.preventDefault(); navigate('/termos'); }} className="hover:text-white transition-colors">Termos</a>
              <button onClick={goLogin} className="hover:text-white transition-colors">Entrar</button>
              <button onClick={goSignUp} className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-4 py-2 rounded-lg hover:from-blue-700 hover:to-purple-700 transition-all">
                Começar agora
              </button>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Landing;
